import React, { useState, useEffect } from "react";
import {
  useHistory,
  Switch,
  Route,
  Redirect,
  useRouteMatch,
} from "react-router-dom";

import { ERRORS } from "../../constants";
import Loading from "../utils/Loading";
import EnsureDestinations from "../destinations/EnsureDestinations";
import CampaignContent from "./content/CampaignContent";
import Scheduler from "./scheduler";
import { useCampaign, useJobs } from "../../state/Storage";
import { editCampaign } from "../../apis/firebase";
import CampaignSteps from "./CampaignSteps";
import CampaignTimeline from "./CampaignTimeline";
import Editor from "./editor";
import CampaignReport from "./CampaignReport";
import Activity from "../utils/icons/Activity";
import { CampaignStats, formatStatName } from "../helpers";

enum TITLE_FORM_STATE {
  EDITING,
  IN_PROGRESS,
  ERROR,
  SUCCESS,
}

export default function CampaignManager() {
  // @ts-ignore
  const match = useRouteMatch("/campaigns/edit/:id/:step");
  const id = match && match.params ? match.params.id : null;
  const jobsManager = useJobs();
  const [campaign] = useCampaign(id);
  const history = useHistory();
  const [error, setError] = useState(null);
  const [formState, setFormState] = useState<TITLE_FORM_STATE>(
    TITLE_FORM_STATE.EDITING
  );

  useEffect(() => {
    let interval;
    if (formState === TITLE_FORM_STATE.SUCCESS) {
      interval = setTimeout(() => {
        if (formState === TITLE_FORM_STATE.SUCCESS) {
          setFormState(TITLE_FORM_STATE.EDITING);
        }
      }, 2000);
    }
    return () => clearTimeout(interval);
  }, [formState]);

  if (!campaign) {
    return (
      <div className="rel mt3 layout center">
        <Loading />
      </div>
    );
  }

  const noContent = campaign.content && campaign.content?.length === 0;

  async function onSubmit(str: string) {
    try {
      setFormState(TITLE_FORM_STATE.IN_PROGRESS);
      if (campaign && campaign.id) {
        await editCampaign(campaign.id, { title: str });
      }
      setFormState(TITLE_FORM_STATE.SUCCESS);
    } catch (err) {
      if (err.message === ERRORS.NO_AUTHENTICATION) {
        history.push("/login");
        return;
      }
      console.error(err);
      setError(new Error("There is a problem saving your post."));
      setFormState(TITLE_FORM_STATE.ERROR);
    }
  }

  return (
    <>
      <div className="rel mt3 layout">
        <input
          defaultValue={campaign.title}
          type="text"
          placeholder="Your title here"
          onBlur={(e) => {
            onSubmit(e.target.value || "Untitled");
          }}
          className="title"
          style={{ marginBottom: 0, padding: 0 }}
        />
        {error && <div className="error mb1">✖ {error.message}</div>}
        {formState === TITLE_FORM_STATE.IN_PROGRESS && (
          <div className="abs" style={{ top: "-16px", right: "30px" }}>
            <Loading size={20} />
          </div>
        )}
        {formState === TITLE_FORM_STATE.SUCCESS && (
          <div className="success abs" style={{ top: "10px", right: "10px" }}>
            ✔
          </div>
        )}
      </div>
      <CampaignStats campaign={campaign} />
      <CampaignSteps />
      <Switch>
        <Route
          path="/campaigns/edit/:id/publishing"
          render={() =>
            noContent ? (
              <Redirect to={`/campaigns/edit/${campaign.id}/content`} />
            ) : (
              <>
                <div>
                  <EnsureDestinations campaign={campaign}>
                    <Scheduler campaign={campaign} jobsManager={jobsManager} />
                    <CampaignTimeline
                      campaign={campaign}
                      jobsManager={jobsManager}
                    />
                  </EnsureDestinations>
                </div>
              </>
            )
          }
        />
        <Route path="/campaigns/edit/:id/content">
          <div className="layout mt2">
            <CampaignContent post={campaign} />
          </div>
        </Route>
        <Route path="/campaigns/edit/:id/editor">
          <div className="layout mt2">
            <Editor
              post={campaign}
              onSuccess={() => {
                history.push(`/campaigns/edit/${campaign.id}/content`);
              }}
            />
          </div>
        </Route>
        <Route path="/campaigns/edit/:id/report">
          <div className="layout mt2">
            <CampaignReport post={campaign} />
          </div>
        </Route>
      </Switch>
    </>
  );
}
